<script setup lang="ts">
import { computed, ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Music2, Lightbulb } from 'lucide-vue-next'
import { useSpotifyStore } from '@/stores/spotify'
import { useHueBridgesStore } from '@/stores/hueBridges'
import LightBridgeManagementModal from '@/components/integrations/LightBridgeManagementModal.vue'

const spotifyStore = useSpotifyStore()
const hueBridgesStore = useHueBridgesStore()

const spotifyConnected = computed(() => spotifyStore.isConnected)
const bridgeCount = computed(() => hueBridgesStore.allBridges.length)

const showBridgeModal = ref(false)

const handleSpotifyAction = async () => {
  if (spotifyConnected.value) {
    await spotifyStore.disconnect()
  } else {
    await spotifyStore.connect()
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Section Header -->
    <div>
      <h3 class="text-sm font-medium">Connected Services</h3>
      <p class="text-xs text-muted-foreground mt-1">Link external services to enhance your experience</p>
    </div>
    
    <!-- Integrations List -->
    <div class="space-y-3">
      <!-- Spotify Card -->
      <div 
        class="relative overflow-hidden rounded-xl border transition-all duration-200"
        :class="spotifyConnected ? 'border-green-500/30 bg-green-500/5' : 'border-border'"
      >
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div 
                class="h-10 w-10 rounded-lg flex items-center justify-center transition-colors"
                :class="spotifyConnected ? 'bg-green-500/20' : 'bg-green-500/10'"
              >
                <Music2 class="h-5 w-5 text-green-500" />
              </div>
              <div>
                <div class="text-sm font-medium flex items-center gap-2">
                  Spotify
                  <span 
                    v-if="spotifyConnected"
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-500/20 text-green-600"
                  >
                    Connected
                  </span>
                </div>
                <div class="text-xs text-muted-foreground">
                  {{ spotifyConnected ? 'Stream music and control playback' : 'Connect to stream music' }}
                </div>
              </div>
            </div>
            <Button
              :variant="spotifyConnected ? 'outline' : 'default'"
              size="sm"
              @click="handleSpotifyAction"
              :class="!spotifyConnected && 'bg-green-600 hover:bg-green-700'"
            >
              {{ spotifyConnected ? 'Disconnect' : 'Connect' }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Hue Bridges Card -->
      <div 
        class="relative overflow-hidden rounded-xl border transition-all duration-200"
        :class="bridgeCount > 0 ? 'border-orange-500/30 bg-orange-500/5' : 'border-border'"
      >
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div 
                class="h-10 w-10 rounded-lg flex items-center justify-center transition-colors"
                :class="bridgeCount > 0 ? 'bg-orange-500/20' : 'bg-orange-500/10'"
              >
                <Lightbulb class="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div class="text-sm font-medium flex items-center gap-2">
                  Philips Hue
                  <span 
                    v-if="bridgeCount > 0"
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-600"
                  >
                    {{ bridgeCount }} Bridge{{ bridgeCount !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div class="text-xs text-muted-foreground">
                  {{ bridgeCount > 0 ? 'Control smart lights and scenes' : 'Connect to control smart lights' }}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" @click="showBridgeModal = true">
              {{ bridgeCount > 0 ? 'Manage' : 'Setup' }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bridge Management Modal -->
    <LightBridgeManagementModal v-model:open="showBridgeModal" />
  </div>
</template>
